import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {OrganisationData} from "types/Organisation";
import OrganisationForm from '../Form';

function MutateOrganisation() {
    const {id} = useParams()
    const {data: organisation, isLoading} = useQuery(
        ['organisations', {id}],
        () => ngage.organisations.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: OrganisationData) => ngage.organisations.store(data))
    const update = useMutation((data: OrganisationData) => ngage.organisations.update(Number(id), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: OrganisationData, {setSubmitting}: FormikHelpers<OrganisationData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('organisations.all')

        setSubmitting(false)
        navigate('/organisations')
    }

    // Handle store mutation
    async function handleStore(values: OrganisationData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Organisation Created", `${values.name} has been created`)
        } catch (error) {
            notify("error", "Organisation Not Created", `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: OrganisationData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Organisation Updated", `${values.name} has been updated`)
            await invalidate(['organisations', {id}])
        } catch (error) {
            notify("error", "Organisation Not Updated", `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Admin': '/', 'Organisations': '/organisations', 'New Organisation': '/organisations/new'}
    const editBreadcrumbs = {'Admin': '/', 'Organisations': '/organisations', 'Edit Organisation': `/organisations/${id}/edit`}

    return (
        <Page title={id ? "Edit Organisation" : "New Organisation"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <OrganisationForm organisation={organisation} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateOrganisation;